import type { EnKey } from "odyssey-lang";
import { timeoutSignal, type TimeoutSignal } from "../timeout-signal.ts";
import { say } from "../words.ts";
import { banSentence } from "./moderation/words.ts";
import { errorLine, requestable } from "./request-check.ts";

/**
 * How the game page reaches the gateway's account endpoints (P1.4-T2, ADR-075): one JSON post with a time
 * limit, sent with credentials only where the kept session's cookie is set, read or cleared, and the page's
 * own sentences for a request that got no usable answer.
 */

/**
 * The refusal codes the page words itself, each with the key of its sentence: the account and look routes answer
 * each with one sentence whatever was asked (ADR-119 decision 3). `rate`, `familyName`, `characterName`, `email` and
 * `accountName` have several, chosen by the gateway, so those are shown as its `message`, as is a code this build
 * does not know. `banned` is worded from the ban's reason and end.
 */
export const WORDED_REFUSALS = {
  body: "account.refused.body",
  busy: "account.refused.busy",
  contentType: "account.refused.contentType",
  credentials: "account.refused.credentials",
  currentPassword: "account.refused.currentPassword",
  disabled: "account.refused.disabled",
  family: "account.refused.familyNeeded",
  frozen: "account.refused.lookFrozen",
  limit: "account.refused.noRoom",
  lingering: "account.refused.lingering",
  link: "account.refused.link",
  look: "account.refused.lookInvalid",
  maintenance: "account.refused.maintenance",
  method: "account.refused.method",
  newPassword: "account.refused.passwordInvalid",
  origin: "account.refused.origin",
  password: "account.refused.passwordInvalid",
  route: "account.refused.route",
  server: "account.refused.unavailable",
  session: "account.refused.sessionGone",
  taken: "account.refused.registrationClashed",
  unavailable: "account.refused.unavailable",
  voice: "account.refused.voiceInvalid",
} as const satisfies Readonly<Record<string, EnKey>>;

/**
 * The page's sentence for a refusal's code, when it words that code itself.
 *
 * @param error The code as the gateway sent it.
 * @returns The sentence, or null for a code the page shows the gateway's sentence for.
 */
function wordedRefusal(error: unknown): string | null {
  return typeof error === "string" && Object.hasOwn(WORDED_REFUSALS, error) ? say(WORDED_REFUSALS[error as keyof typeof WORDED_REFUSALS]) : null;
}

/**
 * An account request's answer: the parsed body on success, or the sentence to show, the status it came with (0 when
 * the server did not answer, or did not answer in time), the gateway's code for the refusal (its `error`, such as
 * `email` or `rate`; empty when the answer carried none) and, for a refused look, the field the gateway named.
 */
export type AuthAnswer = { readonly ok: true; readonly body: unknown } | { readonly ok: false; readonly message: string; readonly status: number; readonly code: string; readonly field?: "skin" | "hair" | "clothes" };

/**
 * How long an account request may take, answer included, before the page gives up on it as a server that
 * did not answer. The gateway answers a request it cannot take at once (a full password hashing gate, a
 * rate limit) rather than queueing it, and hashing takes well under a second, so ten seconds is a stall.
 */
const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Posts a JSON body to one account endpoint. A request that has not answered within `REQUEST_TIMEOUT_MS`
 * is aborted and reads as a server that did not answer, so no caller waits on it for ever: a dialog busy
 * with it leaves busy, and a refresh that stalls ends as one that failed.
 *
 * @param base The account origin.
 * @param route The endpoint name under `/auth/`.
 * @param body The request body: text and flags, and for a look its record of keys.
 * @param credentials Whether to send and take cookies: true for the routes that set, read or clear the kept
 *   session's cookie (a kept login, resume, logout and logout everywhere), false for every other.
 * @returns The parsed body on success, or the server's sentence, the status it came with (0 when the
 *   server did not answer, or did not answer in time) and the gateway's code for the refusal (its `error`,
 *   such as `email` or `rate`; empty when the answer carried none).
 */
export async function post(base: string, route: string, body: Record<string, string | boolean | Readonly<Record<string, string>>>, credentials = false): Promise<AuthAnswer> {
  const url = `${base}/auth/${route}`;
  let limit: TimeoutSignal | null = null;
  let init: RequestInit;
  try {
    limit = timeoutSignal(REQUEST_TIMEOUT_MS);
    init = { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal: limit.signal, ...(credentials ? { credentials: "include" as const } : {}) };
    requestable(url, init);
  } catch (error) {
    limit?.settle();
    console.error(`the ${route} request could not be built and was not sent`, error);
    return { ok: false, message: say("account.request.unsent"), status: 0, code: "" };
  }
  try {
    let response: Response;
    try {
      response = await fetch(url, init);
    } catch (error) {
      console.warn(`the ${route} request got no answer: ${errorLine(error)}`);
      return { ok: false, message: say("account.request.unreachable"), status: 0, code: "" };
    }
    return await answerOf(response);
  } finally {
    limit.settle();
  }
}

/**
 * Reads the gateway's answer to an account request.
 *
 * @param response The answer.
 * @returns The parsed body on success, or the refusal's sentence (the page's for a code of `WORDED_REFUSALS`, else
 *   the gateway's), the status it came with and the gateway's code. A
 *   banned account's refusal (403 `banned`, ADR-056's amendment of 2026-10-04) carries the ban's reason and its end,
 *   which the page words as the ban's notice does, the end on the player's own clock.
 */
async function answerOf(response: Response): Promise<AuthAnswer> {
  if (response.status === 204) {
    return { ok: true, body: null };
  }
  let parsed: unknown = null;
  try {
    parsed = await response.json();
  } catch {
    parsed = null;
  }
  if (response.ok) {
    return { ok: true, body: parsed };
  }
  const { message, error, reason, until, field } = typeof parsed === "object" && parsed !== null ? (parsed as { message?: unknown; error?: unknown; reason?: unknown; until?: unknown; field?: unknown }) : {};
  if (error === "banned" && typeof reason === "string" && (until === null || typeof until === "number")) {
    return { ok: false, message: banSentence(reason, until), status: response.status, code: error };
  }
  const named = field === "skin" || field === "hair" || field === "clothes" ? field : null;
  return { ok: false, message: typeof message === "string" ? (wordedRefusal(error) ?? message) : say("account.request.failed", { status: String(response.status) }), status: response.status, code: typeof error === "string" ? error : "", ...(named === null ? {} : { field: named }) };
}
